import EmptyState from '@/components/EmptyState';
import { useT } from '@/i18n';
import { formatMoney } from '@/utils/money';
import { useIsWideViewport } from '@/utils/useMediaQuery';
import type { TableSession } from '@restaurant/api-client';
import {
  Block,
  BlockTitle,
  Button,
  List,
  ListItem,
  Navbar,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { useTableSession } from '../hooks/useTableOrdering';

/**
 * The collect step - the focused screen for the act of taking the money, and
 * nothing else. Reached from the bill overview once a split plan exists; it
 * owns the screen so the operator can work the table share by share without
 * the overview's survey/manage chrome competing. Each awaiting share taps
 * through to the payment leaf at its fixed amount; collected shares tick off
 * and the tab settles (and auto-closes) when the last is in.
 */
export default function BillCollectPage({
  f7route,
}: {
  f7route: { params: { sessionId: string } };
}) {
  const tAll = useT();
  const t = tAll.tableOrdering.billCollect;
  const sessionId = f7route.params.sessionId;
  const { data: session, isLoading } = useTableSession(sessionId);

  return (
    <Page name="bill-collect">
      <Navbar title={t.collectPayments} backLink={tAll.back} />
      {isLoading || !session ? (
        <Block style={{ textAlign: 'center', paddingTop: 48 }}>
          <Preloader size={32} />
        </Block>
      ) : (
        <Collect session={session} />
      )}
    </Page>
  );
}

function Collect({ session }: { session: TableSession }) {
  const tAll = useT();
  const t = tAll.tableOrdering.billCollect;
  const tPayment = tAll.tableOrdering.payment;
  const isWide = useIsWideViewport();
  const currency = session.currency;
  const balance = Math.max(0, session.totalCents - session.paidCents);
  const settled = session.paidCents >= session.totalCents && session.totalCents > 0;
  const shares = session.payments;
  const collectedCount = shares.filter((p) => p.status === 'PAID').length;
  const methodName = (k: string) => (k === 'CASH' ? tPayment.cash : tPayment.cardTerminal);

  const collect = (paymentId: string) =>
    f7.views.current.router.navigate(
      `/tables/session/${session.id}/bill/payment?paymentId=${paymentId}`,
    );
  // Collecting the last share auto-closes the tab, so once settled there's
  // nothing left to do here - leave the bill subtree for the table it was
  // launched from (floor inspector on wide, session page on phone).
  const exitToSession = () =>
    f7.views.current.router.back(
      isWide ? '/tables' : `/tables/session/${session.id}`,
      // `force` pops the whole /bill subtree to that in-history url; a plain
      // back(url) only relabels the immediately-previous page.
      { force: true },
    );

  // Navigated here with nothing to collect - steer back to setting up a split.
  if (shares.length === 0) {
    return (
      <Block style={{ paddingTop: 8 }}>
        <EmptyState icon="person_2_square_stack" title={t.noSplit} description={t.noSplitDesc} />
        <Button
          large
          round
          fill
          className="margin-top"
          onClick={() =>
            f7.views.current.router.navigate(`/tables/session/${session.id}/bill/split`)
          }
        >
          {t.splitBill}
        </Button>
      </Block>
    );
  }

  return (
    <div className="to-bill">
      <Block className="to-bill-lead">
        <span>{settled ? t.collectedFull : t.collectedCountOf(collectedCount, shares.length)}</span>
        <b>{formatMoney(settled ? session.totalCents : balance, currency)}</b>
      </Block>

      <BlockTitle>{t.shares}</BlockTitle>
      <List dividers strongIos insetIos>
        {shares.map((p, i) => {
          const paid = p.status === 'PAID';
          return (
            <ListItem
              key={p.id}
              link={paid ? undefined : '#'}
              noChevron={paid}
              title={t.shareNum(i + 1)}
              footer={
                paid
                  ? p.tipCents > 0
                    ? t.methodTipFooter(methodName(p.methodKind), formatMoney(p.tipCents, currency))
                    : t.methodCollectedFooter(methodName(p.methodKind))
                  : t.awaiting
              }
              after={formatMoney(p.amountCents, currency)}
              onClick={paid ? undefined : () => collect(p.id)}
            >
              <i slot="media" className={`f7-icons ${paid ? 'to-bill-ic-ok' : 'to-bill-nav-ic'}`}>
                {paid ? 'checkmark_seal_fill' : 'circle'}
              </i>
            </ListItem>
          );
        })}
      </List>

      {settled ? (
        <Block>
          <Button fill large round onClick={exitToSession}>
            {t.done}
          </Button>
        </Block>
      ) : (
        <Block className="to-seat-note text-color-gray">{t.tapNote}</Block>
      )}
      <div style={{ height: 'calc(16px + var(--f7-safe-area-bottom, 0px))' }} />
    </div>
  );
}
